// Sub-parts step — used by weldment parts.
//
// Captures the list of sub-parts that make up the weldment. Per spec/14,
// WeldmentSpec carries `subParts: Part[]` — full nested Part objects.
// For now the wizard treats each sub-part as a lightweight reference:
// part number string + qty per assembly. A real nested-part-picker
// (point at existing Parts via partId, or open a sub-wizard for new
// inline parts) is a future pass.
//
// Persistence shape: we serialize the rows into stub Part objects on
// `weldment.subParts[]` so the field shape matches crmTypes today.
// The stubs only carry .number/.description/.type='machined' — enough
// to round-trip through the save flow but not enough for pricing.
// When the future pass lands, these will get upgraded to real partId
// references.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { Part, WeldmentSpec } from '@/shared/crmTypes';

interface SubPartRow {
  number: string;
  qty: string;
}

function rowFromPart(p: Part): SubPartRow {
  // Future-pass: when subParts becomes partId references, this stub layer
  // will turn into a real lookup. For now we just unpack the cached fields.
  const qtyStr = (p as unknown as { _qtyInAssembly?: number })._qtyInAssembly?.toString() ?? '';
  return { number: p.number ?? '', qty: qtyStr };
}

function partFromRow(r: SubPartRow): Part {
  // Build the minimum-viable Part shape. Baseline fields are zeroed —
  // they get re-stamped by crm.ts when the parent part is persisted.
  // The non-spec `_qtyInAssembly` is a wizard-local extension we'll
  // strip / migrate when the real sub-part flow lands.
  return {
    id: '',
    number: r.number,
    description: '',
    type: 'machined',
    qtyTiers: [],
    createdAt: '',
    updatedAt: '',
    _v: 1,
    _audit: [],
    _deleted: false,
    // wizard-local extension — see comment above
    ...({ _qtyInAssembly: Number.parseFloat(r.qty || '0') } as unknown as object),
  } as Part;
}

export function SubPartsStep({ part, onChange }: StepProps) {
  const rows: SubPartRow[] = (part.weldment?.subParts ?? []).map(rowFromPart);

  function updateRow(i: number, patch: Partial<SubPartRow>) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    persist(next);
  }

  function addRow() {
    persist([...rows, { number: '', qty: '1' }]);
  }

  function removeRow(i: number) {
    persist(rows.filter((_, idx) => idx !== i));
  }

  function persist(next: SubPartRow[]) {
    const weldment: WeldmentSpec = {
      subParts: next.map(partFromRow),
      weldOps: part.weldment?.weldOps ?? [],
    };
    onChange({ weldment });
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-text-mute italic border border-dashed border-line-dark rounded p-4 text-center">
          No sub-parts yet. Add the pieces that get welded together.
        </div>
      )}

      {rows.map((r, i) => (
        <div key={i} className="border border-line rounded p-3 bg-bg/30">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end">
            <Field label={`Sub-part #${i + 1} (part number)`}>
              <input
                type="text"
                value={r.number}
                onChange={(e) => updateRow(i, { number: e.target.value })}
                placeholder="BASE-2245"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
            </Field>
            <Field label="Qty per assembly">
              <NumberInput
                value={r.qty === '' ? undefined : Number.parseFloat(r.qty)}
                onChange={(v) => updateRow(i, { qty: v === undefined ? '' : String(v) })}
                step={1}
                placeholder="1"
                numpadTitle="Qty per assembly"
              />
            </Field>
            <div className="text-right">
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-xs text-text-mute hover:text-orange py-1.5 px-2"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
      >
        + Add sub-part
      </button>

      <div className="text-xs text-text-mute italic">
        Real nested-part picker (point at existing Parts, or open a sub-wizard inline)
        is a future pass.
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
